<script setup>
import { computed, nextTick, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import { useArrowsAiAssistants } from 'dashboard/composables/useArrowsAiAssistants';
import { useArrowsAiServices } from 'dashboard/composables/useArrowsAiServices';
import { useArrowsAiCsvImport } from 'dashboard/composables/useArrowsAiCsvImport';
import {
  buildCsv,
  csvHeadersFor,
  downloadCsv,
} from 'dashboard/helper/arrowsAiCsv';
import PageLayout from 'dashboard/components-next/arrowsAi/PageLayout.vue';
import EmptyState from 'dashboard/components-next/arrowsAi/EmptyState.vue';
import MissingAssistant from 'dashboard/components-next/arrowsAi/MissingAssistant.vue';
import CsvActions from 'dashboard/components-next/arrowsAi/CsvActions.vue';
import BulkSelectBar from 'dashboard/components-next/arrowsAi/assistant/BulkSelectBar.vue';
import ServiceCard from 'dashboard/components-next/arrowsAi/assistant/ServiceCard.vue';
import ServiceDialog from 'dashboard/components-next/arrowsAi/assistant/ServiceDialog.vue';

const { t, locale } = useI18n();
const route = useRoute();
const { getAssistant, rememberAssistant } = useArrowsAiAssistants();
const assistantId = computed(() => Number(route.params.assistantId));
const assistant = computed(() => {
  const current = getAssistant(assistantId.value);
  if (current) rememberAssistant(current.id);
  return current;
});
const {
  services,
  isLoading,
  createService,
  updateService,
  deleteServices,
  importServices,
  exportWorkbook,
} = useArrowsAiServices(assistantId);
const { notifyResult, notifyError } = useArrowsAiCsvImport();
const csvActionsRef = ref(null);
const isImporting = ref(false);
const serviceHeaders = computed(() =>
  csvHeadersFor(
    locale.value,
    ['name', 'price', 'duration', 'description'],
    ['nombre', 'precio', 'tiempo', 'descripcion']
  )
);
const exampleContent = computed(() =>
  buildCsv(serviceHeaders.value, [
    [
      t('ARROWS_AI.SERVICES.EXAMPLE_N1'),
      t('ARROWS_AI.SERVICES.EXAMPLE_P1'),
      t('ARROWS_AI.SERVICES.EXAMPLE_T1'),
      t('ARROWS_AI.SERVICES.EXAMPLE_D1'),
    ],
    [
      t('ARROWS_AI.SERVICES.EXAMPLE_N2'),
      t('ARROWS_AI.SERVICES.EXAMPLE_P2'),
      t('ARROWS_AI.SERVICES.EXAMPLE_T2'),
      t('ARROWS_AI.SERVICES.EXAMPLE_D2'),
    ],
  ])
);

const selectedIds = ref([]);
const selectedLabel = computed(() =>
  t('ARROWS_AI.SERVICES.SELECTED', { count: selectedIds.value.length })
);
const selectedService = ref({});
const dialogRef = ref(null);
const isEmpty = computed(() =>
  Boolean(assistant.value && !isLoading.value && !services.value.length)
);
const ITEMS_PER_PAGE = 25;
const currentPage = ref(1);
const pagedServices = computed(() => {
  const start = (currentPage.value - 1) * ITEMS_PER_PAGE;
  return services.value.slice(start, start + ITEMS_PER_PAGE);
});

watch(
  () => services.value.length,
  total => {
    const maxPage = Math.max(1, Math.ceil(total / ITEMS_PER_PAGE));
    if (currentPage.value > maxPage) currentPage.value = maxPage;
  }
);

const openCreate = () => {
  selectedService.value = {};
  nextTick(() => dialogRef.value.dialogRef.open());
};

const openEdit = service => {
  selectedService.value = service;
  nextTick(() => dialogRef.value.dialogRef.open());
};

const handleSubmit = async payload => {
  if (!payload.name.trim()) {
    useAlert(t('ARROWS_AI.SERVICES.ERROR'));
    return;
  }

  try {
    if (selectedService.value.id) {
      await updateService(selectedService.value.id, payload);
    } else {
      await createService(payload);
    }
    dialogRef.value.dialogRef.close();
    await nextTick();
    useAlert(t('ARROWS_AI.SERVICES.SUCCESS', { name: payload.name.trim() }));
  } catch (error) {
    useAlert(error.message || t('ARROWS_AI.SERVICES.SAVE_ERROR'));
  }
};

const toggleSelect = id => {
  selectedIds.value = selectedIds.value.includes(id)
    ? selectedIds.value.filter(item => item !== id)
    : [...selectedIds.value, id];
};

const exportServices = () => {
  downloadCsv(
    csvHeadersFor(locale.value, 'services.csv', 'servicios.csv'),
    buildCsv(
      serviceHeaders.value,
      services.value.map(item => [
        item.name,
        item.price,
        item.duration,
        item.description,
      ])
    )
  );
};

const handleImport = async file => {
  isImporting.value = true;
  try {
    const data = await importServices(file);
    csvActionsRef.value?.closeImport();
    await nextTick();
    notifyResult(data);
    currentPage.value = 1;
  } catch (error) {
    notifyError(error);
  } finally {
    isImporting.value = false;
  }
};

const removeServices = async ids => {
  try {
    await deleteServices(ids);
    selectedIds.value = selectedIds.value.filter(id => !ids.includes(id));
    useAlert(t('ARROWS_AI.SERVICES.DELETED'));
  } catch (error) {
    useAlert(error.message || t('ARROWS_AI.SERVICES.DELETE_ERROR'));
  }
};
</script>

<template>
  <PageLayout
    :header-title="t('ARROWS_AI.SERVICES.TITLE')"
    :button-label="t('ARROWS_AI.SERVICES.ADD')"
    :is-empty="isEmpty"
    :show-pagination-footer="Boolean(assistant)"
    :current-page="currentPage"
    :total-count="services.length"
    :items-per-page="ITEMS_PER_PAGE"
    @click="openCreate"
    @update:current-page="currentPage = $event"
  >
    <template v-if="assistant" #headerActions>
      <CsvActions
        ref="csvActionsRef"
        :example-filename="t('ARROWS_AI.SERVICES.EXAMPLE_FILE')"
        :example-content="exampleContent"
        :is-importing="isImporting"
        spreadsheet
        @import="handleImport"
        @export="exportServices"
        @export-excel="exportWorkbook"
      />
    </template>
    <template #emptyState>
      <EmptyState
        :title="t('ARROWS_AI.SERVICES.EMPTY_TITLE')"
        :subtitle="t('ARROWS_AI.SERVICES.EMPTY_SUBTITLE')"
        :action-label="t('ARROWS_AI.SERVICES.ADD')"
        @click="openCreate"
      />
    </template>
    <MissingAssistant v-if="!assistant" />
    <div v-else class="flex flex-col gap-4">
      <BulkSelectBar
        v-model="selectedIds"
        :items="pagedServices"
        :select-all-label="t('ARROWS_AI.SERVICES.SELECT_ALL')"
        :unselect-all-label="t('ARROWS_AI.SERVICES.UNSELECT_ALL')"
        :selected-label="selectedLabel"
        :delete-label="t('ARROWS_AI.SERVICES.DELETE')"
        @bulk-delete="removeServices(selectedIds)"
      />
      <ServiceCard
        v-for="service in pagedServices"
        :key="service.id"
        :service="service"
        :selected="selectedIds.includes(service.id)"
        @select="toggleSelect"
        @edit="openEdit"
        @delete="removeServices([$event])"
      />
    </div>
  </PageLayout>
  <ServiceDialog
    ref="dialogRef"
    :service="selectedService"
    @submit="handleSubmit"
  />
</template>
